/**
 * Filter Dependencies
 */
import moment from 'moment'
import Numeral from 'numeral'

/**
 * Establish the non-numeric values that may come back
 *
 * @type {Array}
 */
const oddValues = [
    'Infinity',
    '-Infinity',
    'NaN'
]

/**
 * Digits regex
 *
 * @type {RegExp}
 */
const digitsRE = /(\d{3})(?=\d)/g

/**
 * Handle translating edge cases where Infinity or other unexpected values show up in the payload
 *
 * @param  {Number|String|Null} value
 * @param  {String} format
 * @return {String}
 */
const parseValue = (value, format = null) => {
    switch (value) {
        case 'Infinity':
            return '∞'
        case '-Infinity':
            return '-∞'
        case 'NaN':
            return 'N/A'
        default:
            return Numeral(value).format(format)
    }
}

/**
 * Get the currency postfix
 *
 * @param  {String} unit
 * @return {String}
 */
const getUnitPostfix = (unit = 'dollars') => {
    switch (unit) {
        case 'dollars':
            return ''
        case 'thousands':
            return ' K'
        case 'millions':
            return ' M'
        default:
            return ''
    }
}

/**
 * Currency filter
 *
 * @param  {Number}  val
 * @param  {Number}  decimals
 * @param  {String}  unit
 * @param  {Boolean} includePrefix
 * @return {String}
 */
export const currency = (val, decimals = 2, unit = 'dollars', includePrefix = true) => {
    if (val === 0 || !val) return '—'

    // Establish value
    let value = divideUnit(parseFloat(val), unit)

    // Return if blank
    if (!isFinite(value) || (!value && value !== 0)) return ''

    // Decimal assignment
    decimals = determineDecimals(decimals, unit)

    // Magic
    const stringified = Math.abs(value).toFixed(decimals)
    const _int = decimals
        ? stringified.slice(0, -1 - decimals)
        : stringified
    const i = _int.length % 3
    const head = i > 0
        ? (_int.slice(0, i) + (_int.length > 3 ? ',' : ''))
        : ''
    const _float = decimals
        ? stringified.slice(-1 - decimals)
        : ''
    const output = head +
        _int.slice(i).replace(digitsRE, '$1,') +
        _float

    // Send back the value if positive, wrap the negative output in parentheses
    const result = includePrefix
        ? (value < 0 ? `($${output})` : `$${output}`)
        : (value < 0 ? `(${output})` : output)

    return `${result}${getUnitPostfix(unit)}`
}

/**
 * Divide the unit based on the unit preference
 *
 * @param  {Number} value
 * @param  {String} unit
 * @return {Number}
 */
const divideUnit = (value = 0, unit = 'dollars') => {
    switch (unit) {
        case 'thousands':
            return (value / 1000)
        case 'millions':
            return (value / 1000000)
        default:
            return value
    }
}

/**
 * Determine the decimal scale
 *
 * @param  {Number} decimals
 * @param  {String} unit
 * @return {[type]}
 */
const determineDecimals = (decimals = 2, unit = 'dollars') =>
    unit === 'dollars'
        ? (decimals || 0)
        : 2

/**
 * Format value to percentage string
 *
 * @param  {Integer|Number|Float} value
 * @return {String}
 */
export const percent = value => parseValue(value, '0.00%')

/**
 * Format to a multiple (x) value
 *
 * @param  {Integer|Number|Float} value
 * @return {String}
 */
export const multiple = value => {
    const parsed = parseValue(value, '0.00')

    return oddValues.includes(value) ? parsed : `${parsed}x`
}

/**
 * Determine the MIME type
 *
 * @param  {String} value
 * @return {String}
 */
export const mime = (value) => value.replace('/', '_').replace(/\./g, '-')

/**
 * Convert a date string to a readable format
 *
 * @param  {String} value
 * @return {String}
 */
export const date = (date, format = 'M/D/Y', currentFormat = 'YYYY-MM-DD') =>
    (date !== 'NaN' && date !== null && date !== '')
        ? moment(date, currentFormat).format(format)
        : null

/**
 * Translate the bytes of a file size
 *
 * @param  {Number} bytes
 * @param  {Number} decimals
 * @return {String}
 */
export const bytes = (bytes, decimals) => Numeral(parseFloat(bytes).toFixed(2)).format('0.00b')

/**
 * Translate the number into a thousands comma-separated value
 *
 * @param  {Number} value
 * @return {String}
 */
export const number = value => Numeral(value).format('0,0')

/**
 * Truncate a string by a given max length
 *
 * @param  {String} string
 * @param  {Number} max
 * @return {String}
 */
export const truncate = (string, max = 15) =>
    string.length > max
        ? `${string.substring(0, max)} ...`
        : string

/**
 * Parse an address object and convert it to a string
 *
 * @param  {Object} obj
 * @return {String}
 */
export const address = obj => {
    let arr = [
        obj.address_street_1,
        obj.address_street_2,
        obj.address_city,
        obj.address_state,
        obj.address_zipcode
    ]

    return arr
        .filter(i => !!i)
        .join(', ')
}

/**
 * Return an adjusted string
 *
 * @param  {String} str
 * @return {String}
 */
const convertString = str => str || '—'

/**
 * Return an appropriate mutated format based on a filter parameter
 *
 * @param  {mixed} value
 * @param  {String} filterName
 * @return {String}
 */
export const variableFormat = (value, filterName, decimals = 2, unit = 'dollars', includePrefix = true) => {
    switch (filterName) {
        case 'currency_string': return currency(value, decimals, unit, false)
        case 'currency': return currency(value, decimals, unit, includePrefix)
        case 'monetary': return currency(value, decimals, unit, includePrefix)
        case 'dollar': return currency(value, decimals, unit, includePrefix)
        case 'percentage': return percent(value)
        case 'percent': return percent(value)
        case 'multiple': return multiple(value)
        case 'date': return date(value)
        case 'number': return number(value)
        case 'address': return address(value)
        case 'string': return convertString(value)
        default: return value
    }
}



// WEBPACK FOOTER //
// ./src/filters/index.js